import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Trophy, Loader2, RefreshCw, Check, X, Lock, TrendingUp, ShieldAlert, Swords } from "lucide-react";

const fetchCompetitors = () => api.get("/admin/competitors").then((r) => r.data);
const crawlCompetitors = () => api.post("/admin/competitors/crawl").then((r) => r.data);

export default function CompetitorTopics() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ["competitors"], queryFn: fetchCompetitors });
  const [crawling, setCrawling] = useState(false);

  const crawl = async () => {
    setCrawling(true);
    try {
      await crawlCompetitors();
      setTimeout(() => { qc.invalidateQueries({ queryKey: ["competitors"] }); setCrawling(false); }, 45000);
    } catch { setCrawling(false); }
  };

  if (isLoading || !data) return <div className="flex items-center justify-center py-24 text-slate-400"><Loader2 className="h-6 w-6 animate-spin" /></div>;
  const { competitors, matrix, wins, losses, take_share, crawled, last_crawled } = data;

  return (
    <div data-testid="competitor-topics-root">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-navy flex items-center gap-2"><Trophy className="h-6 w-6 text-launch" /> Competitor Topic Comparison</h1>
          <p className="text-slate-500 mt-1 max-w-3xl">Topic coverage detected from each competitor's own published pages. See where you win, where you're contested, and where to take market share. <span className="text-slate-400">Google/AI rankings need a connected source — not shown.</span></p>
        </div>
        <button onClick={crawl} disabled={crawling} data-testid="competitors-crawl-btn" className="inline-flex items-center gap-2 bg-navy text-white text-sm font-semibold px-4 py-2 rounded-lg disabled:opacity-60">
          {crawling ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />} {crawling ? "Crawling…" : "Crawl Competitors"}
        </button>
      </div>

      {/* Competitor chips */}
      <div className="mt-5 flex flex-wrap gap-2" data-testid="competitor-chips">
        {competitors.map((c) => (
          <a key={c.url} href={c.url} target="_blank" rel="noreferrer" className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm hover:border-launch">
            <span className="font-semibold text-navy">{c.name}</span>
            <span className="ml-2 text-[11px] text-slate-400">{c.page_count} pages{c.crawl_status && c.crawl_status !== "reachable" ? ` · ${c.crawl_status.replace("_", " ")}` : (c.last_crawled ? "" : " · not crawled")}</span>
          </a>
        ))}
      </div>
      {!crawled && <p className="mt-2 text-xs text-amber-600">No crawl data yet — click "Crawl Competitors" to detect their topic coverage from their live sites.</p>}
      {last_crawled && <p className="mt-2 text-[11px] text-slate-400">Last crawled: {new Date(last_crawled).toLocaleString()}</p>}

      {/* Win / Contested / Lose panels */}
      <div className="grid sm:grid-cols-3 gap-3 mt-5">
        <div className="rounded-xl border border-green-200 bg-green-50 p-4" data-testid="wins-panel">
          <h3 className="font-bold text-green-800 flex items-center gap-1 text-sm"><TrendingUp className="h-4 w-4" /> Where We Win ({wins.length})</h3>
          <ul className="mt-2 space-y-1 text-[13px] text-green-900">
            {wins.length ? wins.map((w) => <li key={w.slug}>{w.topic} <span className="text-green-600">· {w.competitor_coverage} rivals</span></li>) : <li className="text-green-600/70">—</li>}
          </ul>
        </div>
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-4" data-testid="takeshare-panel">
          <h3 className="font-bold text-amber-800 flex items-center gap-1 text-sm"><Swords className="h-4 w-4" /> Take Market Share ({take_share.length})</h3>
          <ul className="mt-2 space-y-1 text-[13px] text-amber-900">
            {take_share.length ? take_share.map((t) => <li key={t.slug}>{t.topic} <span className="text-amber-600">· {t.competitor_coverage} rivals</span></li>) : <li className="text-amber-600/70">—</li>}
          </ul>
        </div>
        <div className="rounded-xl border border-red-200 bg-red-50 p-4" data-testid="losses-panel">
          <h3 className="font-bold text-red-800 flex items-center gap-1 text-sm"><ShieldAlert className="h-4 w-4" /> Where We Lose ({losses.length})</h3>
          <ul className="mt-2 space-y-1 text-[13px] text-red-900">
            {losses.length ? losses.map((l) => <li key={l.slug}>{l.topic} <span className="text-red-600">· {l.competitor_coverage} rivals cover it</span></li>) : <li className="text-red-600/70">—</li>}
          </ul>
        </div>
      </div>

      {/* Matrix */}
      <div className="mt-6 rounded-xl border border-slate-200 bg-white overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="competitor-matrix">
            <thead className="bg-slate-50 text-left text-[11px] uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-3 py-3 sticky left-0 bg-slate-50 z-10">Topic</th>
                <th className="px-2 py-3 text-center bg-navy/5 text-navy">Aquila</th>
                {competitors.map((c) => <th key={c.url} className="px-2 py-3 text-center whitespace-nowrap" title={c.name}>{c.name.split(" ")[0]}</th>)}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {matrix.map((r) => (
                <tr key={r.slug} data-testid={`comp-row-${r.slug}`}>
                  <td className="px-3 py-2 font-semibold text-navy sticky left-0 bg-white z-10 whitespace-nowrap">{r.topic}</td>
                  <td className="px-2 py-2 text-center bg-navy/5">
                    {r.us.covered ? <Check className="h-4 w-4 text-green-600 mx-auto" /> : <X className="h-4 w-4 text-red-400 mx-auto" />}
                    {r.us.gravity != null && <span className="block text-[10px] text-slate-400">g{r.us.gravity}</span>}
                  </td>
                  {competitors.map((c) => {
                    const cell = r.competitors[c.name] || { covered: false, depth: 0 };
                    return (
                      <td key={c.url} className="px-2 py-2 text-center">
                        {cell.covered ? <span className="inline-flex flex-col items-center"><Check className="h-4 w-4 text-slate-500" /><span className="text-[10px] text-slate-400">{cell.depth}</span></span> : <X className="h-4 w-4 text-slate-200 mx-auto" />}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <p className="mt-4 text-[11px] text-slate-400 flex items-center gap-1"><Lock className="h-3 w-3" /> Coverage = topics detected in each competitor's public page/URL structure. Ranking & AI-citation comparison unlock with a connected data source.</p>
    </div>
  );
}
